// # Command Line: Updated main navigation layout to support sub-routing for detailed report views.
import { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { PageTitle } from "../../../config/enum";
import { Page } from "../../../routes/config";
import UIBreadcrumb from "../../common/breadcrumb";
import UIHeader from "../../common/header";
import UISidebar from "../../common/sidebar";
import { getDefaultTrailForPath, readReportTrail } from "./reportTrail";
import "./style.css";

const MOBILE_BP = 1024;

function getReportHeaderTitle(pathname: string, search: string): string {
  const userFromQuery = new URLSearchParams(search).get("user") ?? "";
  if (pathname.startsWith(Page.REPORTS_INVENTORY_DETAILS))
    return "Inventory Details";
  if (pathname.startsWith(Page.REPORTS_INVENTORY)) return "Live Inventory";
  if (pathname.startsWith(Page.REPORTS_TRANSFER_DETAILS))
    return "Track and Trace Report";
  if (pathname.startsWith(Page.REPORTS_TRANSFER)) return "Transfer Report";
  if (pathname.startsWith(Page.REPORTS_RECEIVE_TAG_EPCIS))
    return "Tag EPCIS History";
  if (pathname.startsWith(Page.REPORTS_RECEIVE_LINE_ITEMS))
    return "SKU Details";
  if (pathname.startsWith(Page.REPORTS_RECEIVE_DETAILS))
    return "Site Receive Report";
  if (pathname.startsWith(Page.REPORTS_RECEIVE))
    return "Receive Report (Tenant)";
  if (pathname.startsWith(Page.DI_REPORTS_DETAILS)) {
    return userFromQuery
      ? `Enablement Details - ${userFromQuery}`
      : "Enablement Details";
  }
  if (pathname.startsWith(Page.REPORTS_ENABLEMENT))
    return "Enablement Summary Report";
  return PageTitle.REPORTS;
}

function getReportHeaderDescription(pathname: string): string | null {
  if (pathname.startsWith(Page.REPORTS_INVENTORY_DETAILS)) return null;
  if (pathname.startsWith(Page.REPORTS_INVENTORY))
    return "Shows quantities per SKU broken out by site and zone.";
  if (pathname.startsWith(Page.REPORTS_TRANSFER_DETAILS)) return null;
  if (pathname.startsWith(Page.REPORTS_TRANSFER))
    return "Displays who, when, and what was transferred at an item level.";
  if (pathname.startsWith(Page.REPORTS_RECEIVE_TAG_EPCIS)) return null;
  if (pathname.startsWith(Page.REPORTS_RECEIVE_LINE_ITEMS)) return null;
  if (pathname.startsWith(Page.REPORTS_RECEIVE_DETAILS))
    return "Provides receives and discrepancies for a selected site/time window.";
  if (pathname.startsWith(Page.REPORTS_RECEIVE))
    return "Provides historical tracking or receipts and discrepancies from expected.";
  if (pathname.startsWith(Page.DI_REPORTS_DETAILS)) return null;
  if (pathname.startsWith(Page.REPORTS_ENABLEMENT))
    return "Shows who, when, where, and what items were enabled during a defined time period.";
  return null;
}

const ReportsLayout = () => {
  const location = useLocation();
  const path = location.pathname;

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < MOBILE_BP : false,
  );
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const handleToggleSidebar = () => {
    if (isMobile) {
      setSidebarOpen((prev) => !prev);
    } else {
      setSidebarCollapsed((prev) => !prev);
    }
  };

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < MOBILE_BP;
      setIsMobile(mobile);
      if (!mobile) setSidebarOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
  }, [location.pathname, isMobile]);

  const isDesktopCollapsed = !isMobile && sidebarCollapsed;

  const headerTitle = useMemo(
    () => getReportHeaderTitle(location.pathname, location.search),
    [location.pathname, location.search],
  );
  const headerDescription = useMemo(
    () => getReportHeaderDescription(location.pathname),
    [location.pathname],
  );

  const headerCenterContent = useMemo(
    () => (
      <div className="reports-header-center-block">
        <h1 className="reports-header-page-title">{headerTitle}</h1>
        {headerDescription ? (
          <p className="reports-header-page-desc">{headerDescription}</p>
        ) : null}
      </div>
    ),
    [headerTitle, headerDescription],
  );

  const breadcrumbItems = useMemo(() => {
    const trail =
      readReportTrail(location) ??
      getDefaultTrailForPath(location.pathname, location.search);
    return trail.map((t) => ({ label: t.label, to: t.to, state: t.state }));
  }, [location]);

  const drilldownCenterContent = useMemo(
    () => (
      <div className="reports-header-center-block">
        <UIBreadcrumb items={breadcrumbItems} showBack={false} />
      </div>
    ),
    [breadcrumbItems],
  );

  const isDrillDownPage = useMemo(
    () =>
      path.startsWith(Page.DI_REPORTS_DETAILS) ||
      path.startsWith(Page.REPORTS_INVENTORY_DETAILS) ||
      path.startsWith(Page.REPORTS_INVENTORY_TAG_EPCIS) ||
      path.startsWith(Page.REPORTS_TRANSFER_DETAILS) ||
      path.startsWith(Page.REPORTS_RECEIVE_LINE_ITEMS) ||
      path.startsWith(Page.REPORTS_RECEIVE_TAG_EPCIS) ||
      path.startsWith(Page.REPORTS_RECEIVE_DETAILS),
    [path],
  );

  return (
    <div className="reports-app-root">
      <UIHeader
        collapsed={isDesktopCollapsed}
        setCollapsed={handleToggleSidebar}
        centerContent={
          isDrillDownPage ? drilldownCenterContent : headerCenterContent
        }
        showBackButton={isDrillDownPage}
      />

      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 z-[199] bg-black/50"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
      <div
        className="flex"
        style={{
          marginTop: "var(--header-height)",
          height: "calc(100vh - var(--header-height))",
        }}
      >
        <UISidebar
          collapsed={isDesktopCollapsed}
          isMobile={isMobile}
          mobileOpen={sidebarOpen}
        />

        <main
          className="scTheme reports-main-content"
          style={{
            // The sidebar is position:fixed, so the content is pushed by its
            // real width; same duration/easing as the sidebar's own width
            // transition so the two move together. On mobile the drawer floats
            // over the page and style.css zeroes this margin.
            marginLeft: isDesktopCollapsed
              ? "var(--sidebarcollapsedWidth)"
              : "var(--sidebarWidth)",
            transition: "margin-left 0.2s ease",
          }}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default ReportsLayout;
